import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react'
import { Sidebar, DashboardHeader } from './layout'
import { useLanguage } from '../i18n'
import { api, fetchV2Data } from '../services/api'
import { calculateUserRisk } from '../services/userRisk'
import type {
  DashboardData,
  VulnerabilitySummary,
  MFAStatus,
  ShadowITApp,
  DeviceCompliance,
  CompliancePolicy,
  ZTAScore,
  ScoreTimeline,
  CrowdStrikeAlert,
  AlertsSummary,
  FleetInsightsSummary,
  MITRETacticData,
  VulnDrillDownResponse,
  JumpCloudUser,
  SocRadarPDFReport,
  UnifiedAlert,
} from '../types'
// html2canvas removed — executive PDF is now generated via jsPDF
// (see src/lib/reports/executivePDF.ts) instead of a DOM screenshot.

// Lazy-loaded enhanced components
// Legacy CrowdStrike-only AlertsTimeline — replaced by UnifiedAlertsTimeline.
// Kept importable for backward compat in case a future dedicated section
// needs just the EDR feed, but not rendered in the main dashboard anymore.
const Device360Drawer = lazy(() => import('./dashboard/device360/Device360Drawer'))
const MITREHeatmap = lazy(() => import('./dashboard/analytics/MITREHeatmap'))
const BrowserExtensionsAudit = lazy(() => import('./dashboard/analytics/BrowserExtensionsAudit'))
const USBMonitoring = lazy(() => import('./dashboard/analytics/USBMonitoring'))
const SoftwareInventory = lazy(() => import('./dashboard/software/SoftwareInventory'))
const PolicyComplianceMatrix = lazy(() => import('./dashboard/compliance/PolicyComplianceMatrix'))
const AssetComplianceGrid = lazy(() => import('./dashboard/compliance/AssetComplianceGrid'))
const AccessGraph = lazy(() => import('./dashboard/analytics/AccessGraph'))
const SocRadarReport = lazy(() => import('./dashboard/socradar/SocRadarReport'))
const ExposureMap = lazy(() => import('./dashboard/analytics/ExposureMap'))
const SocRadarIncidents = lazy(() => import('./dashboard/socradar/SocRadarIncidents'))
const SocRadarASM = lazy(() => import('./dashboard/socradar/SocRadarASM'))
const DarkWebMonitoring = lazy(() => import('./dashboard/socradar/DarkWebMonitoring'))
const User360Drawer = lazy(() => import('./dashboard/user360/User360Drawer'))
const TenantBillingSection = lazy(() => import('./dashboard/billing/TenantBillingSection'))
const BrandingSettings = lazy(() => import('./dashboard/settings/BrandingSettings'))
const HeroPosture = lazy(() => import('./dashboard/HeroPosture'))
const TopThreeActions = lazy(() => import('./dashboard/TopThreeActions'))
const UnifiedAlertsTimeline = lazy(() => import('./dashboard/alerts/UnifiedAlertsTimeline'))

interface DashboardV2Props {
  tenantSlug: string
  tenants?: Array<{ id: string; slug: string; name: string; logo_url?: string; domain?: string; primary_color?: string; socradar_enabled?: number; actions_enabled?: number; tenant_role?: string }>
  /** Global user.role from /api/auth/me (jarix_admin / tenant_admin /
   *  tenant_viewer). Used so a Jarix admin can mutate tenant-scoped
   *  settings even without a tenant_users row. */
  userRole?: string
  onTenantChange?: (slug: string) => void
}

export default function DashboardV2({ tenantSlug, tenants = [], userRole, onTenantChange }: DashboardV2Props) {
  const { t, language } = useLanguage()
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('7d')
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date())
  const [activeSection, setActiveSection] = useState('dashboard')
  const [showCVEModal, setShowCVEModal] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('jarix-sidebar-collapsed') === 'true'
    }
    return false
  })
  const dashboardRef = useRef<HTMLDivElement>(null)

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => {
      const newValue = !prev
      localStorage.setItem('jarix-sidebar-collapsed', String(newValue))
      return newValue
    })
  }, [])

  // Handle sidebar navigation — replaces content instead of scrolling
  const handleSectionChange = useCallback((sectionId: string) => {
    // Special action for export
    if (sectionId === 'export') {
      handleExportPDF()
      return
    }

    setActiveSection(sectionId)

    // Scroll content area to top
    const content = document.querySelector('.dashboard-content')
    if (content) content.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  // Dashboard data
  const [data, setData] = useState<DashboardData | null>(null)

  // V2 data - fetched from real APIs
  const [vulnerabilities, setVulnerabilities] = useState<VulnerabilitySummary | null>(null)
  const [mfaStatus, setMfaStatus] = useState<MFAStatus | null>(null)
  const [shadowIT, setShadowIT] = useState<ShadowITApp[]>([])
  const [compliance, setCompliance] = useState<DeviceCompliance | null>(null)
  const [ztaScore, setZtaScore] = useState<ZTAScore | null>(null)
  const [scoreTimeline, setScoreTimeline] = useState<ScoreTimeline | null>(null)

  // Enhanced data - Phase 1-4
  const [alerts, setAlerts] = useState<CrowdStrikeAlert[]>([])
  const [_alertsSummary, setAlertsSummary] = useState<AlertsSummary | null>(null)
  const [_fleetInsights, setFleetInsights] = useState<FleetInsightsSummary | null>(null)
  const [mitreHeatmap, setMitreHeatmap] = useState<MITRETacticData[]>([])
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [socradarReport, setSocradarReport] = useState<SocRadarPDFReport | null>(null)
  const [unifiedAlerts, setUnifiedAlerts] = useState<UnifiedAlert[]>([])

  // Vuln drill-down state
  const [vulnDrillSeverity, setVulnDrillSeverity] = useState<'critical' | 'high' | 'medium' | 'low' | null>(null)
  const [vulnDrillData, setVulnDrillData] = useState<VulnDrillDownResponse | null>(null)
  const [vulnDrillLoading, setVulnDrillLoading] = useState(false)
  const [showMfaUsers, setShowMfaUsers] = useState(false)

  const fetchAllData = useCallback(async () => {
    try {
      // Fetch main dashboard data and V2 data in parallel
      const [dashboardResponse, v2Data] = await Promise.all([
        api.getDashboard(timeRange, tenantSlug),
        fetchV2Data(tenantSlug, timeRange).catch(err => {
          console.warn('Error fetching V2 data:', err)
          return null
        })
      ])

      if (dashboardResponse.success && dashboardResponse.data) {
        const apiData = dashboardResponse.data as {
          stats?: { totalDevices: number; protectedDevices: number; totalUsers: number; threatsBlocked?: number };
          detections?: Array<{ detection_id: string; device_id: string; severity: string; status: string; created_timestamp: string }>;
          devices?: Array<{ device_id: string; hostname: string; platform_name: string }>;
          users?: Array<{ id: string; email: string; firstname?: string; lastname?: string; activated: boolean; suspended: boolean; created: string }>;
          securityScore?: number | { overall: number; breakdown: Record<string, number>; grade: string };
          jumpcloud?: {
            // userActivationStatus comes from getUserActivationStatus() and contains { activated, suspended }
            userActivationStatus?: { activated: number; suspended: number };
            // userConnectionStatus is for SYSTEMS (connected/disconnected), NOT users.
            // Kept here only to document the distinction — do NOT read suspended from it.
            userConnectionStatus?: { connected: number; disconnected: number };
          };
        }

        // Handle securityScore being either a number or an object
        let scoreValue = 0
        if (apiData.securityScore !== undefined) {
          if (typeof apiData.securityScore === 'number') {
            scoreValue = apiData.securityScore
          } else if (typeof apiData.securityScore === 'object' && apiData.securityScore !== null) {
            scoreValue = apiData.securityScore.overall || 0
          }
        }

        setData({
          totalDevices: apiData.stats?.totalDevices || 0,
          protectedDevices: apiData.stats?.protectedDevices || 0,
          totalUsers: apiData.stats?.totalUsers || 0,
          securityScore: scoreValue,
          threatsBlocked: apiData.stats?.threatsBlocked || 0,
          mttr: 0, // Will be calculated from real data when available
          // Suspended users still present in the directory. The previous code
          // read from userConnectionStatus which is a SYSTEMS metric (no
          // "suspended" field), so this was always 0.
          suspendedUsersWithAccess: apiData.jumpcloud?.userActivationStatus?.suspended || 0,
          detections: (apiData.detections || []).map(d => ({
            id: d.detection_id,
            device_id: d.device_id,
            hostname: 'Device',
            platform: 'Windows',
            severity: d.severity || 'medium',
            status: d.status || 'new',
            type: 'Malware',
            tactic: 'Execution',
            timestamp: d.created_timestamp || new Date().toISOString()
          })),
          users: (apiData.users || []).map(u => ({
            ...u,
            username: u.email?.split('@')[0] || u.email || 'unknown'
          })),
          devices: (apiData.devices || []).map((d: any) => ({
            device_id: d.device_id,
            hostname: d.hostname,
            platform_name: d.platform_name,
            os_version: d.os_version || '',
            os_build: d.os_build || '',
            status: d.status || 'normal',
            last_seen: d.last_seen || new Date().toISOString(),
            local_ip: d.local_ip || '',
            connection_ip: d.connection_ip || '',
            mac_address: d.mac_address || '',
            external_ip: d.external_ip || '',
            agent_version: d.agent_version || '',
          }))
        })
      }

      // Update V2 state from real API data
      if (v2Data) {
        setVulnerabilities(v2Data.vulnerabilities)
        setMfaStatus(v2Data.mfaStatus)
        setShadowIT(v2Data.shadowIT)
        setCompliance(v2Data.compliance)
        setZtaScore(v2Data.ztaScore)
        setScoreTimeline(v2Data.scoreTimeline)
        // Enhanced data
        if (v2Data.alerts) setAlerts(v2Data.alerts)
        if (v2Data.alertsSummary) setAlertsSummary(v2Data.alertsSummary)
        if (v2Data.fleetInsights) setFleetInsights(v2Data.fleetInsights)
        if (v2Data.mitreHeatmap) setMitreHeatmap(v2Data.mitreHeatmap)
      }

      setLastUpdate(new Date())
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    }
  }, [tenantSlug, timeRange])

  useEffect(() => {
    setIsLoading(true)
    fetchAllData().finally(() => setIsLoading(false))
  }, [fetchAllData])

  // Fetch SocRadar PDF report in the background. Not blocking: used to
  // surface credential-leak and Russian-Market counts in TopThreeActions
  // and Z4. Swallowed on failure so it never breaks the dashboard.
  useEffect(() => {
    let cancelled = false
    api.getSocRadarReport(tenantSlug)
      .then(res => {
        if (cancelled) return
        if (res.success && res.data) {
          const d = res.data as any
          setSocradarReport((d.data ?? d) as SocRadarPDFReport)
        }
      })
      .catch(err => console.warn('[socradar] report fetch failed:', err))
    return () => { cancelled = true }
  }, [tenantSlug])

  // Fetch unified alerts feed (CrowdStrike + JumpCloud) in parallel with
  // the rest. Non-blocking — if it fails we just show an empty timeline.
  useEffect(() => {
    let cancelled = false
    api.getUnifiedAlerts(tenantSlug, timeRange)
      .then(res => {
        if (cancelled) return
        if (res.success && res.data) {
          const d = res.data as any
          const payload = (d.data ?? d) as UnifiedAlert[]
          setUnifiedAlerts(Array.isArray(payload) ? payload : [])
        }
      })
      .catch(err => console.warn('[alerts/unified] fetch failed:', err))
    return () => { cancelled = true }
  }, [tenantSlug, timeRange])

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await fetchAllData()
    setIsRefreshing(false)
  }

  const handleExportPDF = async () => {
    try {
      // Executive PDF report — Tanda 3. Uses the data already loaded in
      // state, no extra API calls.
      const { generateExecutivePDF, imageUrlToDataUrl } = await import('../lib/reports/executivePDF')

      // Load Jarix brand logos once (negative for the cover gradient,
      // normal for any internal page that needs it). Both fall back to
      // a wordmark inside the generator if the fetch fails.
      const [jarixLogoNegDataUrl, jarixLogoDataUrl] = await Promise.all([
        imageUrlToDataUrl('/branding/jarix-logo-neg-h.png').catch(() => ''),
        imageUrlToDataUrl('/branding/jarix-logo-h.png').catch(() => ''),
      ])

      // Tenant logo: the same source the sidebar uses (explicit logo_url
      // from D1, else Google favicon fallback). Gracefully skipped on
      // failure — we just show the initial chip.
      let tenantLogoDataUrl: string | undefined
      if (tenantLogoUrl) {
        try {
          tenantLogoDataUrl = await imageUrlToDataUrl(tenantLogoUrl)
        } catch { /* optional */ }
      }

      const tenantName = currentTenant?.name || tenantSlug

      const blob = await generateExecutivePDF({
        tenantName,
        tenantSlug,
        tenantDomain: currentTenant?.domain?.split(',')[0],
        jarixLogoNegDataUrl,
        jarixLogoDataUrl,
        tenantLogoDataUrl,
        language: (language === 'es' ? 'es' : 'en'),
        period: timeRange,
        data,
        mfaStatus,
        vulnerabilities,
        unifiedAlerts,
        socradarReport,
        shadowIT,
        scoreTimeline,
        securityScore,
        grade,
        // Pass the already-computed breakdown so the PDF uses the SAME
        // values the dashboard renders — no re-computation, no drift.
        scoreBreakdown,
        compliance,
      })

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const stamp = new Date().toISOString().split('T')[0]
      link.download = `Jarix_Security_Report_${tenantSlug}_${stamp}.pdf`
      link.href = url
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exporting PDF:', error)
      // As an absolute last resort, surface a simple alert so the user
      // knows the click did something. Intentionally no fallback to the
      // old PNG — if the new generator fails we want to see it, not
      // silently regress to the screenshot behavior.
      alert(language === 'es'
        ? 'No se pudo generar el informe PDF. Intenta nuevamente o contacta a Jarix.'
        : 'Could not generate the PDF report. Please retry or contact Jarix.')
    }
  }

  // Handle severity bar click → fetch drill-down data
  const handleSeverityClick = useCallback(async (severity: 'critical' | 'high' | 'medium' | 'low') => {
    setVulnDrillSeverity(severity)
    setVulnDrillLoading(true)
    setVulnDrillData(null)
    try {
      const res = await api.getVulnerabilitiesBySeverity(tenantSlug, severity)
      if (res.success && res.data) {
        const d = res.data as any
        const payload = d.data || d
        setVulnDrillData(payload as VulnDrillDownResponse)
      }
    } catch (err) {
      console.error('Error fetching vuln drill-down:', err)
    } finally {
      setVulnDrillLoading(false)
    }
  }, [tenantSlug])

  const securityScore = data?.securityScore || 0
  const getGrade = (score: number) => {
    if (score >= 90) return { letter: 'A', color: '#3fb950' }
    if (score >= 80) return { letter: 'B', color: '#58a6ff' }
    if (score >= 70) return { letter: 'C', color: '#d29922' }
    if (score >= 60) return { letter: 'D', color: '#ffa94d' }
    return { letter: 'F', color: '#f85149' }
  }
  const grade = getGrade(securityScore)

  // Calculate breakdown scores from real data
  const scoreBreakdown = {
    // Protection: based on protected devices ratio
    protection: data?.totalDevices && data.totalDevices > 0
      ? Math.round((data.protectedDevices / data.totalDevices) * 100)
      : 0,
    // User Security: based on MFA adoption
    userSecurity: mfaStatus?.adoptionRate ?? 0,
    // Vulnerabilities: inverse of critical vulns (100 - normalized vuln count)
    vulnerabilities: vulnerabilities?.critical?.count !== undefined
      ? Math.max(0, 100 - (vulnerabilities.critical.count * 5) - (vulnerabilities?.high?.count ?? 0) * 2)
      : 0,
    // Connectivity: based on ZTA score if available
    connectivity: ztaScore?.averageScore ?? 0,
  }
  const threatCount = alerts.length > 0
    ? alerts.filter(a => a.max_severity >= 4).length
    : (data?.detections?.filter(d => d.severity === 'Critical' || d.severity === 'High').length || 0)
  const currentTenant = tenants.find(t => t.slug === tenantSlug)
  const tenantName = currentTenant?.name || tenantSlug
  const tenantDomain = currentTenant?.domain?.split(',')[0]
  // logoOverride lets BrandingSettings push the new logo into the
  // header/sidebar immediately on save, without waiting for /api/auth/me
  // to refresh the parent's tenants[] array. `null` = explicit clear,
  // `undefined` = no override (use the tenants[] value).
  const [logoOverride, setLogoOverride] = useState<string | null | undefined>(undefined)
  const persistedLogoUrl = logoOverride !== undefined ? logoOverride : currentTenant?.logo_url
  const tenantLogoUrl = persistedLogoUrl
    || (tenantDomain ? `https://www.google.com/s2/favicons?domain=${tenantDomain}&sz=128` : undefined)
  const socradarEnabled = !!(currentTenant as any)?.socradar_enabled
  const actionsEnabled = !!(currentTenant as any)?.actions_enabled
  // tenant_admin for this tenant can execute actions. jarix_admin is covered server-side.
  const canExecuteActions = (currentTenant as any)?.tenant_role === 'admin'

  return (
    <div className={`dashboard-v2 ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar
        tenantName={tenantName}
        tenantSlug={tenantSlug}
        tenantLogoUrl={tenantLogoUrl}
        tenants={tenants}
        onTenantChange={onTenantChange}
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        threatCount={threatCount}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
        socradarEnabled={socradarEnabled}
        hasMitreData={mitreHeatmap.length > 0}
      />

      <main className="main-content-v2">
        <DashboardHeader
          tenantName={tenantName}
          tenantLogoUrl={tenantLogoUrl}
          lastUpdate={lastUpdate}
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          onExportPDF={handleExportPDF}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
        />

        <div id="dashboard-content" className="dashboard-content" ref={dashboardRef}>
          {isLoading ? (
            <LoadingState />
          ) : (
            <>
              {/* === Main Dashboard View === */}
              {activeSection === 'dashboard' && (
                <>
                  {/* Z1: Hero — score + grade ring + Jarix-managed stats.
                      Replaces the 72px PostureHeader strip with a full
                      first-viewport block. The ring + sparkline pick up
                      tenants.primary_color so each client opens to a
                      panel that visibly belongs to them. */}
                  <Suspense fallback={<div className="hero-posture" style={{ opacity: 0.4 }} />}>
                    <HeroPosture
                      score={securityScore}
                      grade={grade}
                      scoreTimeline={scoreTimeline}
                      primaryColor={(currentTenant as { primary_color?: string } | undefined)?.primary_color}
                      data={data}
                      timeRange={timeRange}
                      alertsReviewed={unifiedAlerts.length}
                    />
                  </Suspense>

                  {/* Z2: Top 3 priority actions */}
                  <Suspense fallback={<div className="top-actions" style={{ minHeight: '180px', opacity: 0.4 }} />}>
                    <TopThreeActions
                      data={data}
                      mfaStatus={mfaStatus}
                      vulnerabilities={vulnerabilities}
                      shadowIT={shadowIT}
                      socradarReport={socradarReport}
                      onNavigate={(sectionId) => handleSectionChange(sectionId)}
                    />
                  </Suspense>

                  {/* Row 2: Metrics */}
                  <div className="grid-4">
                    <MetricCard icon="device" variant="success" value={`${data?.protectedDevices ?? 0}/${data?.totalDevices ?? 0}`} label={t.protectedDevices} subtitle={t.withCrowdStrike} onClick={() => handleSectionChange('devices')} />
                    <MetricCard icon="users" variant="info" value={data?.totalUsers ?? 0} label={t.usersLabel} subtitle={t.fromJumpCloud} onClick={() => handleSectionChange('users')} />
                    <MetricCard icon="lock" variant={((mfaStatus?.adoptionRate ?? 0) >= 90) ? 'success' : 'warning'} value={`${mfaStatus?.adoptionRate ?? 0}%`} label={t.mfaAdoptionLabel} subtitle={t.goalMFA} onClick={() => handleSectionChange('mfa')} />
                    <MetricCard icon="alert" variant="warning" value={(vulnerabilities?.critical?.count ?? 0) + (vulnerabilities?.high?.count ?? 0) + (vulnerabilities?.medium?.count ?? 0) + (vulnerabilities?.low?.count ?? 0)} label={t.totalVulnerabilities} subtitle={t.fromSpotlight} onClick={() => handleSectionChange('vulnerabilities')} />
                  </div>

                  {/* Row 3: Vulnerabilities + MFA */}
                  <div className="grid-2 scroll-section">
                    <div id="section-vulnerabilities">
                      <VulnsCard vulns={vulnerabilities} onShowCVEDetails={() => setShowCVEModal(true)} onSeverityClick={handleSeverityClick} />
                    </div>
                    <div id="section-mfa">
                      <MFACard mfa={mfaStatus} showUsers={showMfaUsers} onToggleUsers={() => setShowMfaUsers(prev => !prev)} />
                    </div>
                  </div>

                  {/* Row 4: Shadow IT + Compliance */}
                  <div className="grid-2 scroll-section">
                    <ShadowCard apps={shadowIT} />
                    <ComplianceCard compliance={compliance} />
                  </div>

                  {/* Row 5: Alerts summary card. The full timeline lives in
                      the dedicated 'threats' section — duplicating it here
                      was redundant and visually heavy. The summary shows
                      count, severity bars and the 5 most recent items;
                      clicking anywhere routes to the dedicated section. */}
                  {(unifiedAlerts.length > 0 || alerts.length > 0) && (
                    <div className="scroll-section">
                      <RecentAlertsSummary
                        alerts={unifiedAlerts}
                        onSeeAll={() => handleSectionChange('threats')}
                      />
                    </div>
                  )}

                  {/* Row 6: Cyber Intelligence Assessment (SocRadar Report).
                      Dark web tiles route to the dedicated 'dark-web'
                      section for tenants with their own SocRadar account
                      (the section that has the deeper drill-down). For
                      others the same Cyber Risk Report IS the deepest
                      view, so we leave the click no-op. */}
                  <div className="scroll-section">
                    <Suspense fallback={<div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />}>
                      <ThreatIntelSection
                        tenantSlug={tenantSlug}
                        onDarkWebClick={socradarEnabled ? () => handleSectionChange('dark-web') : undefined}
                      />
                    </Suspense>
                  </div>

                  {/* Row 7: Timeline */}
                  <div className="scroll-section">
                    <TimelineCard timeline={scoreTimeline} />
                  </div>
                </>
              )}

              {/* === Dedicated Sections (replace dashboard content) === */}

              {activeSection === 'threats' && (
                <Suspense fallback={<div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />}>
                  {/* Use the unified feed (CrowdStrike + JumpCloud) so the sidebar
                      Alerts section matches what the dashboard top-level shows.
                      Previously this rendered AlertsDedicatedSection which was
                      CrowdStrike-only and looked empty for tenants whose activity
                      comes primarily from JumpCloud Directory Insights. */}
                  <UnifiedAlertsTimeline
                    alerts={unifiedAlerts}
                    onDeviceClick={(deviceId) => setSelectedDeviceId(deviceId)}
                    maxVisible={50}
                  />
                </Suspense>
              )}

              {activeSection === 'users' && (
                <UsersDedicatedSection
                  data={data}
                  mfaStatus={mfaStatus}
                  onUserClick={(id) => setSelectedUserId(id)}
                />
              )}

              {activeSection === 'devices' && (
                <DevicesDedicatedSection data={data} onDeviceClick={(aid) => setSelectedDeviceId(aid)} />
              )}

              {activeSection === 'vulnerabilities' && (
                <div className="scroll-section">
                  <VulnsCard vulns={vulnerabilities} onShowCVEDetails={() => setShowCVEModal(true)} onSeverityClick={handleSeverityClick} />
                </div>
              )}

              {activeSection === 'mfa' && (
                <div className="scroll-section">
                  <MFACard mfa={mfaStatus} showUsers={true} onToggleUsers={() => setShowMfaUsers(prev => !prev)} />
                </div>
              )}

              {activeSection === 'mitre' && (
                <Suspense fallback={<div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />}>
                  <MITREHeatmap data={mitreHeatmap} />
                </Suspense>
              )}

              {activeSection === 'compliance' && (
                <Suspense fallback={<div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />}>
                  <ComplianceSection tenantSlug={tenantSlug} />
                </Suspense>
              )}

              {activeSection === 'software' && (
                <Suspense fallback={<div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />}>
                  <SoftwareSection tenantSlug={tenantSlug} totalDevices={data?.totalDevices} />
                </Suspense>
              )}

              {activeSection === 'extensions' && (
                <Suspense fallback={<div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />}>
                  <ExtensionsSection tenantSlug={tenantSlug} />
                </Suspense>
              )}

              {activeSection === 'usb' && (
                <Suspense fallback={<div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />}>
                  <USBSection tenantSlug={tenantSlug} />
                </Suspense>
              )}

              {activeSection === 'access' && (
                <Suspense fallback={<div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />}>
                  <AccessSection tenantSlug={tenantSlug} />
                </Suspense>
              )}

              {activeSection === 'threat-intel' && (
                <Suspense fallback={<div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />}>
                  <ThreatIntelSection
                    tenantSlug={tenantSlug}
                    onDarkWebClick={socradarEnabled ? () => handleSectionChange('dark-web') : undefined}
                  />
                </Suspense>
              )}

              {activeSection === 'exposure' && socradarEnabled && (
                <Suspense fallback={<div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />}>
                  <ExposureSection tenantSlug={tenantSlug} />
                </Suspense>
              )}

              {activeSection === 'socradar-incidents' && socradarEnabled && (
                <Suspense fallback={<div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />}>
                  <SocRadarIncidentsSection tenantSlug={tenantSlug} />
                </Suspense>
              )}

              {activeSection === 'socradar-asm' && socradarEnabled && (
                <Suspense fallback={<div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />}>
                  <SocRadarASMSection tenantSlug={tenantSlug} />
                </Suspense>
              )}

              {activeSection === 'dark-web' && socradarEnabled && (
                <Suspense fallback={<div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />}>
                  <DarkWebSection tenantSlug={tenantSlug} />
                </Suspense>
              )}

              {activeSection === 'history' && (
                <div className="scroll-section">
                  <TimelineCard timeline={scoreTimeline} />
                </div>
              )}

              {/* Sprint 4 — tenant-facing billing portal. Read-only;
                  a tenant_viewer sees the same data as tenant_admin
                  because it's their own billing history and does not
                  expose any internal Jarix metadata. */}
              {activeSection === 'billing' && (
                <Suspense fallback={<div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />}>
                  <TenantBillingSection tenantSlug={tenantSlug} />
                </Suspense>
              )}

              {activeSection === 'branding' && (
                <Suspense fallback={<div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />}>
                  <BrandingSettings
                    tenantSlug={tenantSlug}
                    tenantName={tenantName}
                    currentLogoUrl={tenantLogoUrl}
                    tenantRole={(currentTenant as { tenant_role?: string } | undefined)?.tenant_role}
                    isJarixAdmin={userRole === 'jarix_admin'}
                    onSaved={(url) => setLogoOverride(url)}
                  />
                </Suspense>
              )}
            </>
          )}
        </div>


        {/* CVE Details Modal - uses real CVE data from API */}
        {showCVEModal && (
          <CVEDetailsModal
            cves={vulnerabilities?.exploitedCVEs ?? []}
            onClose={() => setShowCVEModal(false)}
          />
        )}

        {/* Vulnerability Drill-Down Modal */}
        {vulnDrillSeverity && (
          <VulnSeverityDrillDown
            severity={vulnDrillSeverity}
            data={vulnDrillData}
            loading={vulnDrillLoading}
            onClose={() => { setVulnDrillSeverity(null); setVulnDrillData(null) }}
            onDeviceClick={(aid) => { setVulnDrillSeverity(null); setVulnDrillData(null); setSelectedDeviceId(aid) }}
          />
        )}

        {/* Device 360 Drawer */}
        {selectedDeviceId && (
          <Suspense fallback={null}>
            <Device360Drawer
              deviceId={selectedDeviceId}
              tenantSlug={tenantSlug}
              onClose={() => setSelectedDeviceId(null)}
            />
          </Suspense>
        )}

        {/* User 360 Drawer */}
        {selectedUserId && (() => {
          const user = (data?.users || []).find(u => u.id === selectedUserId)
          if (!user) return null
          const noMfaIds = new Set((mfaStatus?.usersWithoutMFA || []).map(u => u.id))
          const noMfaEmails = new Set((mfaStatus?.usersWithoutMFA || []).map(u => u.email?.toLowerCase()))
          const hasMFA = !(noMfaIds.has(user.id) || (!!user.email && noMfaEmails.has(user.email.toLowerCase())))
          const mfaType = getMfaType(user)
          return (
            <Suspense fallback={null}>
              <User360Drawer
                user={user}
                hasMFA={hasMFA}
                mfaType={mfaType}
                tenantSlug={tenantSlug}
                actionsEnabled={actionsEnabled}
                canExecuteActions={canExecuteActions}
                onClose={() => setSelectedUserId(null)}
                onActionSuccess={() => fetchAllData()}
              />
            </Suspense>
          )
        })()}
      </main>
    </div>
  )
}

// Shared platform icon helper — returns clean SVG instead of emoji
function PlatformIcon({ platform }: { platform: string }) {
  const p = (platform || '').toLowerCase()
  if (p.includes('mac') || p.includes('darwin')) return (
    <svg className="platform-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  )
  if (p.includes('windows')) return (
    <svg className="platform-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 12V6.75l6-1.32v6.48L3 12zm17-9v8.75l-10 .08V5.21L20 3zm-17 9.25l6-.09v6.81l-6-1.08V12.25zM20 21l-10-1.91V12.2l10 .09V21z"/>
    </svg>
  )
  if (p.includes('linux')) return (
    <svg className="platform-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.368 1.884 1.43.199.015.399-.003.6-.066.799-.2.993-.866 1.16-1.39.078-.26.139-.468.282-.56.142-.09.363-.135.636-.18.255-.06.53-.06.83-.2.16-.06.32-.203.44-.403.126-.2.21-.46.24-.67.01-.11.013-.2.01-.27l-.002-.07-.044-.63c-.034-.46-.075-.89-.124-1.204-.2-.09-.38-.222-.513-.3-.133-.078-.283-.283-.33-.49-.2-.21-.3-.4-.3-.603 0-.12.028-.26.108-.408a.418.418 0 01-.035-.144 1.52 1.52 0 01-.035-.26c.003-.12.013-.26.083-.412a.42.42 0 00-.012-.45c-.092-.158-.218-.303-.358-.423-.14-.12-.28-.24-.38-.38-.1-.14-.17-.29-.17-.45 0-.05.012-.11.038-.18.04-.07.048-.13.048-.2a.462.462 0 00-.09-.27c-.09-.13-.21-.25-.36-.36-.13-.12-.28-.23-.43-.36-.14-.13-.27-.28-.36-.44l-.16-.36c-.19-.45-.39-.9-.63-1.25-.4-.58-.87-1.14-1.26-1.61-.19-.24-.37-.47-.52-.7a6.062 6.062 0 01-.31-.54c-.24-.49-.39-1.08-.31-2.05C14.76 3.83 15.39.72 12.504 0z"/>
    </svg>
  )
  return (
    <svg className="platform-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8m-4-4v4"/>
    </svg>
  )
}

// Components
function LoadingState() {
  return (
    <div className="animate-pulse">
      <div className="grid-1-2 mb-6">
        <div className="card-v2 h-48" style={{ background: 'var(--bg-card)' }} />
        <div className="card-v2 h-48" style={{ background: 'var(--bg-card)' }} />
      </div>
      <div className="grid-4 mb-6">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="metric-card-v2 h-32" style={{ background: 'var(--bg-secondary)' }} />
        ))}
      </div>
    </div>
  )
}

function MetricCard({ icon, variant, value, label, subtitle, trend, onClick }: { icon: string; variant: string; value: string | number; label: string; subtitle: string; trend?: string; onClick?: () => void }) {
  const icons: Record<string, React.ReactNode> = {
    shield: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    alert: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
    clock: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,
    device: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8m-4-4v4"/></svg>,
    users: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    lock: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  }

  return (
    <div className={`metric-card-v2 ${onClick ? 'clickable' : ''}`} onClick={onClick}>
      {trend && <div className="metric-trend up">{trend}</div>}
      <div className={`metric-icon-v2 ${variant}`}>{icons[icon]}</div>
      <div className={`metric-value-v2 ${variant === 'success' ? 'success' : ''}`}>{value}</div>
      <div className="metric-label-v2">{label}</div>
      <div className="metric-subtitle-v2">{subtitle}</div>
    </div>
  )
}

// CVE Details Modal
interface CVEModalProps {
  cves: Array<{
    cveId: string
    affectedDevices: number
    description: string
    patchAvailable: boolean
    severity: 'critical' | 'high' | 'medium' | 'low'
  }>
  onClose: () => void
}

function CVEDetailsModal({ cves, onClose }: CVEModalProps) {
  const { t } = useLanguage()
  const severityColors: Record<string, { bg: string; text: string; label: string }> = {
    critical: { bg: 'var(--status-critical-bg)', text: 'var(--status-critical)', label: t.criticalLabel },
    high: { bg: 'var(--status-high-bg)', text: 'var(--status-high)', label: t.highLabel },
    medium: { bg: 'var(--status-medium-bg)', text: 'var(--status-medium)', label: t.mediumLabel },
    low: { bg: 'var(--status-low-bg)', text: 'var(--status-low)', label: t.lowLabel },
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content cve-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-row">
            <span className="status-badge" style={{ background: 'var(--status-critical-bg)', color: 'var(--status-critical)' }}>
              {cves.length} {t.cvesActive}
            </span>
            <span className="modal-source">CrowdStrike Spotlight</span>
          </div>
          <button className="modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <h2 className="modal-title">{t.cvesExploitedActively}</h2>
        <p className="modal-description">{t.cvesExploitedInWild}</p>

        <div className="modal-section">
          <h3 className="modal-section-title">{t.detectedVulnerabilities}</h3>
          <div className="cve-list">
            {cves.map((cve) => {
              const severity = severityColors[cve.severity] || severityColors.medium
              return (
                <div key={cve.cveId} className="cve-item">
                  <div className="cve-item-header">
                    <a
                      href={`https://nvd.nist.gov/vuln/detail/${cve.cveId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cve-id"
                    >
                      {cve.cveId}
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/>
                      </svg>
                    </a>
                    <span className="status-badge" style={{ background: severity.bg, color: severity.text }}>
                      {severity.label}
                    </span>
                  </div>
                  <p className="cve-description">{cve.description}</p>
                  <div className="cve-meta">
                    <span className="cve-devices">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="3" width="20" height="14" rx="2"/>
                        <path d="M8 21h8M12 17v4"/>
                      </svg>
                      {cve.affectedDevices} {cve.affectedDevices !== 1 ? t.devicesAffected : t.deviceAffected}
                    </span>
                    <span className={`cve-patch ${cve.patchAvailable ? 'available' : 'unavailable'}`}>
                      {cve.patchAvailable ? (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 12l2 2 4-4"/>
                            <circle cx="12" cy="12" r="10"/>
                          </svg>
                          {t.patchAvailable}
                        </>
                      ) : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M15 9l-6 6M9 9l6 6"/>
                          </svg>
                          {t.noPatch}
                        </>
                      )}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="modal-section">
          <h3 className="modal-section-title">{t.recommendations}</h3>
          <ul className="recommendations-list">
            <li className="recommendation-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/>
              </svg>
              {t.applyPatchesImmediately}
            </li>
            <li className="recommendation-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/>
              </svg>
              {t.implementMitigation}
            </li>
            <li className="recommendation-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/>
              </svg>
              {t.monitorAffectedDevices}
            </li>
          </ul>
        </div>

        <div className="modal-actions">
          <button className="modal-btn secondary" onClick={onClose}>
            {t.close}
          </button>
          <a
            href="https://falcon.crowdstrike.com/spotlight/vulnerabilities"
            target="_blank"
            rel="noopener noreferrer"
            className="modal-btn primary"
          >
            {t.viewInCrowdStrike}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/>
            </svg>
          </a>
        </div>
      </div>
    </div>
  )
}

// Blocked Threats Modal
function VulnsCard({ vulns, onShowCVEDetails, onSeverityClick }: { vulns: VulnerabilitySummary | null; onShowCVEDetails: () => void; onSeverityClick?: (severity: 'critical' | 'high' | 'medium' | 'low') => void }) {
  const { t, language } = useLanguage()
  // Use real data from API, default to 0 if no data
  const data = {
    critical: { count: vulns?.critical?.count ?? 0, change: vulns?.critical?.change ?? 0 },
    high: { count: vulns?.high?.count ?? 0, change: vulns?.high?.change ?? 0 },
    medium: { count: vulns?.medium?.count ?? 0, change: vulns?.medium?.change ?? 0 },
    low: { count: vulns?.low?.count ?? 0, change: vulns?.low?.change ?? 0 }
  }
  const maxCount = Math.max(data.critical.count, data.high.count, data.medium.count, data.low.count, 1) // Min 1 to avoid division by zero

  // Use real CVE data from API
  const exploitedCVEs = vulns?.exploitedCVEs ?? []
  const cveCount = exploitedCVEs.length
  const firstCVE = exploitedCVEs[0]
  const notLicensed = vulns?.licensed === false

  return (
    <div className="card-v2">
      <div className="card-v2-header">
        <div className="card-v2-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>
          {t.vulnerabilitiesSpotlight}
        </div>
        <span className="card-v2-subtitle">{notLicensed ? t.spotlightNotLicensedBadge : t.last30d}</span>
      </div>
      <div className="card-v2-content">
        {notLicensed ? (
          <div className="vuln-not-licensed">
            <div className="vuln-not-licensed-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <div>
              <h4 className="vuln-not-licensed-title">{t.spotlightNotLicensedTitle}</h4>
              <p className="vuln-not-licensed-body">{t.spotlightNotLicensedBody}</p>
            </div>
          </div>
        ) : (
          <>
            <VulnBar label={t.critical} count={data.critical.count} max={maxCount} severity="critical" change={data.critical.change} onClick={() => onSeverityClick?.('critical')} />
            <VulnBar label={t.high} count={data.high.count} max={maxCount} severity="high" change={data.high.change} onClick={() => onSeverityClick?.('high')} />
            <VulnBar label={t.medium} count={data.medium.count} max={maxCount} severity="medium" change={data.medium.change} onClick={() => onSeverityClick?.('medium')} />
            <VulnBar label={t.low} count={data.low.count} max={maxCount} severity="low" change={data.low.change} onClick={() => onSeverityClick?.('low')} />

            {cveCount > 0 && (
              <div className="cve-alert">
                <div className="cve-alert-content">
                  <div className="cve-alert-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
                  </div>
                  <div className="cve-alert-text">
                    <h4>{cveCount} CVE{cveCount !== 1 ? 's' : ''} {t.cvesExploitedActively}</h4>
                    <p>{firstCVE.cveId} ({firstCVE.description}) {t.affectsDevices} {firstCVE.affectedDevices} {language === 'es' ? 'dispositivos' : 'devices'}. {firstCVE.patchAvailable ? t.patchAvailable + '.' : t.noPatch + '.'}</p>
                  </div>
                </div>
                <button className="cve-alert-btn" onClick={onShowCVEDetails}>{t.viewDetails}</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function VulnBar({ label, count, max, severity, change, onClick }: { label: string; count: number; max: number; severity: string; change: number; onClick?: () => void }) {
  const width = (count / max) * 100
  return (
    <div className={`vuln-bar-container ${onClick ? 'vuln-bar-clickable' : ''}`} onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined} onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick() } : undefined}>
      <span className="vuln-label">• {label}</span>
      <div className="vuln-bar">
        <div className={`vuln-bar-fill ${severity}`} style={{ width: `${width}%` }}>
          <span className="vuln-count">{count}</span>
        </div>
      </div>
      <span className={`vuln-change ${change > 0 ? 'negative' : change < 0 ? 'positive' : ''}`}>
        {change > 0 ? `+${change}` : change < 0 ? String(change) : '-'} ↑
      </span>
    </div>
  )
}

function VulnSeverityDrillDown({ severity, data, loading, onClose, onDeviceClick }: {
  severity: 'critical' | 'high' | 'medium' | 'low'
  data: VulnDrillDownResponse | null
  loading: boolean
  onClose: () => void
  onDeviceClick: (aid: string) => void
}) {
  const { t } = useLanguage()
  const [expandedCVEs, setExpandedCVEs] = useState<Set<string>>(() => new Set())

  // Auto-expand CVEs with ≤5 devices once data loads
  useEffect(() => {
    if (data?.cves) {
      const autoExpand = new Set<string>()
      data.cves.forEach(cve => {
        if (cve.affectedDevices.length <= 5) autoExpand.add(cve.cveId)
      })
      setExpandedCVEs(autoExpand)
    }
  }, [data])

  const toggleCVE = (cveId: string) => {
    setExpandedCVEs(prev => {
      const next = new Set(prev)
      if (next.has(cveId)) next.delete(cveId)
      else next.add(cveId)
      return next
    })
  }

  const severityConfig: Record<string, { bg: string; text: string; label: string; subtitleKey: string }> = {
    critical: { bg: 'var(--status-critical-bg)', text: 'var(--status-critical)', label: t.critical, subtitleKey: 'CVSS >= 9.0' },
    high: { bg: 'var(--status-high-bg)', text: 'var(--status-high)', label: t.high, subtitleKey: 'CVSS 7.0 - 8.9' },
    medium: { bg: 'var(--status-medium-bg)', text: 'var(--status-medium)', label: t.medium, subtitleKey: 'CVSS 4.0 - 6.9' },
    low: { bg: 'var(--status-low-bg, rgba(63,185,80,0.15))', text: 'var(--status-low, #3fb950)', label: t.low, subtitleKey: 'CVSS < 4.0' },
  }
  const config = severityConfig[severity]

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content vuln-drilldown-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-title-row">
            <span className="vuln-drilldown-severity-badge" style={{ background: config.bg, color: config.text }}>
              {config.label}
            </span>
            <span className="modal-source">CrowdStrike Spotlight</span>
          </div>
          <button className="modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <h2 className="modal-title">
          {data?.totalVulnerabilities ?? '...'} {t.vulnDrillTitle} {config.label}
        </h2>
        <p className="modal-description">{config.subtitleKey} — {t.vulnDrillSubtitle}</p>

        {/* Content */}
        {loading ? (
          <div className="vuln-drilldown-loading">
            <div className="vuln-drilldown-spinner" />
            <span>{t.vulnDrillLoading}</span>
          </div>
        ) : !data || data.cves.length === 0 ? (
          <div className="vuln-drilldown-empty">{t.vulnDrillNoCVEs}</div>
        ) : (
          <div className="vuln-drilldown-cve-list">
            {data.cves.map(cve => {
              const isExpanded = expandedCVEs.has(cve.cveId)
              return (
                <div key={cve.cveId} className="vuln-drilldown-cve-item">
                  <div className="vuln-drilldown-cve-header" onClick={() => toggleCVE(cve.cveId)}>
                    <div className="vuln-drilldown-cve-left">
                      <svg className={`vuln-drilldown-cve-chevron ${isExpanded ? 'expanded' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 18l6-6-6-6"/>
                      </svg>
                      <a
                        href={`https://nvd.nist.gov/vuln/detail/${cve.cveId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="vuln-drilldown-cve-id"
                        onClick={e => e.stopPropagation()}
                      >
                        {cve.cveId}
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: 4, verticalAlign: 'middle' }}>
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/>
                        </svg>
                      </a>
                      {cve.exploitable && (
                        <span className="vuln-drilldown-cve-exploitable">EXPLOITED</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {cve.affectedDevices.length} {cve.affectedDevices.length === 1 ? 'device' : 'devices'}
                      </span>
                      <span className="vuln-drilldown-cve-score" style={{ background: config.bg, color: config.text }}>
                        CVSS {cve.baseScore.toFixed(1)}
                      </span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="vuln-drilldown-cve-body">
                      {cve.description && (
                        <p className="vuln-drilldown-cve-desc">{cve.description}</p>
                      )}
                      <h4 style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', margin: '8px 0 4px' }}>
                        {t.vulnDrillAffectedDevices} ({cve.affectedDevices.length}):
                      </h4>
                      <table className="vuln-drilldown-devices-table">
                        <thead>
                          <tr>
                            <th>Hostname</th>
                            <th>IP</th>
                            <th>OS</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cve.affectedDevices.map(device => (
                            <tr key={device.aid}>
                              <td>
                                <span
                                  className="vuln-drilldown-hostname-link"
                                  onClick={() => onDeviceClick(device.aid)}
                                >
                                  {device.hostname}
                                </span>
                              </td>
                              <td>{device.localIp || '—'}</td>
                              <td>{device.osVersion || '—'}</td>
                              <td>{device.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Footer */}
        <div className="modal-actions">
          <button className="modal-btn secondary" onClick={onClose}>
            {t.close}
          </button>
          <a
            href="https://falcon.eu-1.crowdstrike.com/spotlight/vulnerabilities"
            target="_blank"
            rel="noopener noreferrer"
            className="modal-btn primary"
          >
            {t.vulnDrillViewInCS}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/>
            </svg>
          </a>
        </div>
      </div>
    </div>
  )
}

function MFACard({ mfa, showUsers, onToggleUsers }: { mfa: MFAStatus | null; showUsers?: boolean; onToggleUsers?: () => void }) {
  const { t, language } = useLanguage()
  // Use real data from API, default to 0 if no data
  const totalUsers = mfa?.totalUsers ?? 0
  const usersWithMFA = mfa?.usersWithMFA ?? 0
  const percentage = mfa?.adoptionRate ?? (totalUsers > 0 ? Math.round((usersWithMFA / totalUsers) * 100) : 0)
  const totp = mfa?.methods?.totp ?? 0
  const push = mfa?.methods?.push ?? 0
  const withoutMFA = mfa?.usersWithoutMFA?.length ?? 0
  const usersWithoutMFAList = mfa?.usersWithoutMFA ?? []

  return (
    <div className="card-v2">
      <div className="card-v2-header">
        <div className="card-v2-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          {t.mfaStatus}
        </div>
        <span className="card-v2-badge" style={{ background: 'var(--status-low-bg)', color: 'var(--status-low)' }}>{t.goalMFA}</span>
      </div>
      <div className="card-v2-content">
        <div className="mfa-container">
          <div className="mfa-donut">
            <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }}>
              <circle cx="50" cy="50" r="40" fill="none" stroke="var(--bg-elevated)" strokeWidth="10" />
              <circle cx="50" cy="50" r="40" fill="none" stroke="#3fb950" strokeWidth="10" strokeLinecap="round" strokeDasharray={`${percentage * 2.51} 251`} transform="rotate(-90 50 50)" />
            </svg>
            <div className="mfa-donut-value">
              <div className="mfa-percentage">{percentage}%</div>
              <div className="mfa-label">{t.adoption}</div>
            </div>
          </div>
          <div className="mfa-legend">
            <div className="mfa-legend-item">
              <span className="mfa-legend-label"><span className="mfa-legend-dot" style={{ background: '#3fb950' }} />{t.usersWithMFA}</span>
              <span className="mfa-legend-value">{usersWithMFA}/{totalUsers}</span>
            </div>
            <div className="mfa-legend-item">
              <span className="mfa-legend-label"><span className="mfa-legend-dot" style={{ background: 'var(--text-muted)' }} />{t.totpAuthenticator}</span>
              <span className="mfa-legend-value">{totp}</span>
            </div>
            <div className="mfa-legend-item">
              <span className="mfa-legend-label"><span className="mfa-legend-dot" style={{ background: 'var(--accent-primary)' }} />{t.pushNotifications}</span>
              <span className="mfa-legend-value">{push}</span>
            </div>
            <div className="mfa-legend-item" style={{ cursor: withoutMFA > 0 ? 'pointer' : undefined }} onClick={withoutMFA > 0 ? onToggleUsers : undefined}>
              <span className="mfa-legend-label" style={{ color: 'var(--status-critical)' }}><span className="mfa-legend-dot" style={{ background: 'var(--status-critical)' }} />{t.withoutMFAActive}</span>
              <span className="mfa-legend-value" style={{ color: 'var(--status-critical)', display: 'flex', alignItems: 'center', gap: 4 }}>
                {withoutMFA}
                {withoutMFA > 0 && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: showUsers ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                    <path d="M6 9l6 6 6-6"/>
                  </svg>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Expandable list of users without MFA */}
        {showUsers && usersWithoutMFAList.length > 0 && (
          <div style={{ marginTop: 16, borderTop: '1px solid var(--border-color)', paddingTop: 12 }}>
            <h4 style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', margin: '0 0 8px' }}>
              {language === 'es' ? 'Usuarios sin MFA' : 'Users without MFA'} ({usersWithoutMFAList.length})
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 200, overflowY: 'auto' }}>
              {usersWithoutMFAList.map((user, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: 'var(--bg-elevated)', borderRadius: 6, fontSize: 12 }}>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{user.name || user.email}</span>
                  <span style={{ color: user.status === 'active' ? 'var(--accent-success)' : 'var(--text-tertiary)', fontSize: 11 }}>
                    {user.status === 'active' ? (language === 'es' ? 'Activo' : 'Active') : (language === 'es' ? 'Suspendido' : 'Suspended')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function ShadowCard({ apps }: { apps: ShadowITApp[] }) {
  const { t } = useLanguage()
  const [failedLogos, setFailedLogos] = React.useState<Set<string>>(new Set())

  // Extended app type with JumpCloud fields
  type ExtendedApp = ShadowITApp & {
    status?: 'NEWLY_DISCOVERED' | 'APPROVED' | 'UNAPPROVED' | 'IGNORED';
    app_domains?: string[];
  }

  // Common domains for logo resolution (name fragment -> domain)
  const commonDomains: Record<string, string> = {
    'gmail': 'google.com', 'outlook': 'microsoft.com', 'microsoft365': 'microsoft.com',
    'googledocs': 'google.com', 'googledrive': 'google.com', 'googlesheets': 'google.com',
    'googleslides': 'google.com', 'googlemeet': 'google.com', 'googlecalendar': 'google.com',
    'slack': 'slack.com', 'notion': 'notion.so', 'figma': 'figma.com',
    'github': 'github.com', 'gitlab': 'gitlab.com', 'jira': 'atlassian.com',
    'compass': 'atlassian.com', 'confluence': 'atlassian.com', 'bitbucket': 'bitbucket.org',
    'trello': 'trello.com', 'asana': 'asana.com',
    'zoom': 'zoom.us', 'meet': 'google.com', 'teams': 'microsoft.com',
    'dropbox': 'dropbox.com', 'drive': 'google.com', 'onedrive': 'microsoft.com',
    'salesforce': 'salesforce.com', 'hubspot': 'hubspot.com', 'zendesk': 'zendesk.com',
    'intercom': 'intercom.com', 'mailchimp': 'mailchimp.com', 'sendgrid': 'sendgrid.com',
    'aws': 'aws.amazon.com', 'azure': 'azure.microsoft.com', 'gcp': 'cloud.google.com',
    'vercel': 'vercel.com', 'netlify': 'netlify.com', 'heroku': 'heroku.com',
    'datadog': 'datadoghq.com', 'newrelic': 'newrelic.com', 'sentry': 'sentry.io',
    'sumologic': 'sumologic.com', 'splunk': 'splunk.com',
    'linear': 'linear.app', 'monday': 'monday.com', 'clickup': 'clickup.com',
    'canva': 'canva.com', 'miro': 'miro.com', 'loom': 'loom.com',
    'lucidchart': 'lucidchart.com', 'lucid': 'lucid.co',
    'calendly': 'calendly.com', 'typeform': 'typeform.com', 'airtable': 'airtable.com',
    'zapier': 'zapier.com', 'make': 'make.com', 'n8n': 'n8n.io',
    'stripe': 'stripe.com', 'paypal': 'paypal.com', 'square': 'squareup.com',
    'quickbooks': 'quickbooks.intuit.com', 'xero': 'xero.com', 'freshbooks': 'freshbooks.com',
    '1password': '1password.com', 'lastpass': 'lastpass.com', 'bitwarden': 'bitwarden.com',
    'keeper': 'keepersecurity.com',
    'okta': 'okta.com', 'auth0': 'auth0.com', 'onelogin': 'onelogin.com',
    'crowdstrike': 'crowdstrike.com', 'jumpcloud': 'jumpcloud.com', 'cloudflare': 'cloudflare.com',
    'openai': 'openai.com', 'anthropic': 'anthropic.com', 'chatgpt': 'openai.com',
    'claude': 'anthropic.com', 'copilot': 'github.com', 'gemini': 'google.com',
    'docker': 'docker.com', 'circleci': 'circleci.com', 'spacelift': 'spacelift.io',
    'opsgenie': 'opsgenie.com', 'pagerduty': 'pagerduty.com',
    'whatsapp': 'whatsapp.com', 'telegram': 'telegram.org', 'discord': 'discord.com',
    'grammarly': 'grammarly.com', 'docusign': 'docusign.com', 'adobe': 'adobe.com',
  }

  // App category mapping (name fragment -> category)
  const appCategories: Record<string, string> = {
    'gmail': 'Email', 'outlook': 'Email', 'thunderbird': 'Email',
    'microsoft365': 'Productivity', 'googledocs': 'Productivity', 'googledrive': 'Cloud Storage',
    'googlesheets': 'Productivity', 'googleslides': 'Productivity', 'googlecalendar': 'Productivity',
    'drive': 'Cloud Storage', 'onedrive': 'Cloud Storage', 'dropbox': 'Cloud Storage',
    'jira': 'Project Management', 'compass': 'Project Management', 'asana': 'Project Management',
    'trello': 'Project Management', 'linear': 'Project Management', 'monday': 'Project Management',
    'clickup': 'Project Management',
    'slack': 'Communication', 'teams': 'Communication', 'zoom': 'Communication',
    'meet': 'Communication', 'discord': 'Communication', 'whatsapp': 'Communication',
    'telegram': 'Communication',
    'github': 'DevOps', 'gitlab': 'DevOps', 'bitbucket': 'DevOps', 'circleci': 'DevOps',
    'docker': 'DevOps', 'spacelift': 'DevOps', 'vercel': 'DevOps', 'netlify': 'DevOps',
    'heroku': 'DevOps',
    'notion': 'Productivity', 'confluence': 'Documentation', 'loom': 'Communication',
    'figma': 'Design', 'canva': 'Design', 'miro': 'Design', 'lucidchart': 'Design',
    'lucid': 'Design',
    'salesforce': 'CRM', 'hubspot': 'CRM',
    'aws': 'Cloud Infrastructure', 'azure': 'Cloud Infrastructure', 'gcp': 'Cloud Infrastructure',
    'cloudflare': 'Cloud Infrastructure',
    '1password': 'Security', 'lastpass': 'Security', 'bitwarden': 'Security',
    'keeper': 'Security', 'crowdstrike': 'Security',
    'okta': 'Identity', 'jumpcloud': 'Identity', 'auth0': 'Identity', 'onelogin': 'Identity',
    'datadog': 'Monitoring', 'sumologic': 'Monitoring', 'newrelic': 'Monitoring',
    'sentry': 'Monitoring', 'splunk': 'Monitoring',
    'opsgenie': 'Incident Management', 'pagerduty': 'Incident Management',
    'stripe': 'Payments', 'paypal': 'Payments', 'square': 'Payments',
    'quickbooks': 'Finance', 'xero': 'Finance', 'freshbooks': 'Finance',
    'zapier': 'Automation', 'make': 'Automation', 'n8n': 'Automation',
    'openai': 'AI', 'chatgpt': 'AI', 'anthropic': 'AI', 'claude': 'AI',
    'copilot': 'AI', 'gemini': 'AI', 'grammarly': 'AI',
    'zendesk': 'Support', 'intercom': 'Support',
    'mailchimp': 'Marketing', 'sendgrid': 'Marketing',
    'docusign': 'Productivity', 'adobe': 'Design', 'airtable': 'Productivity',
    'calendly': 'Productivity', 'typeform': 'Productivity',
  }

  // Resolve category for an app using the mapping
  const getAppCategory = (app: ExtendedApp): string => {
    const cleanName = app.name.toLowerCase().replace(/[^a-z0-9]/g, '')
    const matched = Object.entries(appCategories).find(([key]) => cleanName.includes(key))
    if (matched) return matched[1]
    // Fall back to API-provided category, or 'SaaS' as last resort
    return (app.category && app.category !== 'AI/SaaS') ? app.category : 'SaaS'
  }

  // Get logo URL from app domain
  const getLogoUrl = (app: ExtendedApp): string | null => {
    // Try app_domains first
    if (app.app_domains && app.app_domains.length > 0) {
      const domain = app.app_domains[0].replace(/^https?:\/\//, '').replace(/\/.*$/, '')
      return `https://www.google.com/s2/favicons?sz=128&domain=${domain}`
    }
    // Fallback: try to guess domain from app name
    const cleanName = app.name.toLowerCase().replace(/[^a-z0-9]/g, '')
    const matchedDomain = Object.entries(commonDomains).find(([key]) => cleanName.includes(key))
    return matchedDomain ? `https://www.google.com/s2/favicons?sz=128&domain=${matchedDomain[1]}` : null
  }

  // Get initials as fallback
  const getInitials = (name: string) => {
    const words = name.split(/[\s-_]+/)
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase()
    return name.slice(0, 2).toUpperCase()
  }

  // Count by status
  const counts = {
    unapproved: apps.filter(a => (a as ExtendedApp).status === 'UNAPPROVED').length,
    new: apps.filter(a => (a as ExtendedApp).status === 'NEWLY_DISCOVERED').length,
    approved: apps.filter(a => (a as ExtendedApp).status === 'APPROVED').length,
  }
  const needsAttention = counts.unapproved + counts.new

  // Sort: unapproved first, then new, then approved
  const sortedApps = [...apps].sort((a, b) => {
    const order: Record<string, number> = { 'UNAPPROVED': 0, 'NEWLY_DISCOVERED': 1, 'APPROVED': 2, 'IGNORED': 3 }
    return (order[(a as ExtendedApp).status || ''] ?? 4) - (order[(b as ExtendedApp).status || ''] ?? 4)
  }) as ExtendedApp[]

  // Visible tile count — 12 fills two rows on a 6-up grid at desktop
  // and one row on tablet at 4-up. Under that, "Ver todas" pushes to
  // the JumpCloud SaaS console.
  const visibleApps = sortedApps.slice(0, 12)
  const approvedRate = apps.length > 0 ? Math.round((counts.approved / apps.length) * 100) : 0
  const jumpcloudUrl = 'https://console.jumpcloud.com/userconsole#/saas-management'

  // Status → CSS modifier on the tile (drives border color + corner badge)
  const statusMod = (s?: string) =>
    s === 'UNAPPROVED' ? 'review' :
    s === 'NEWLY_DISCOVERED' ? 'new' :
    s === 'APPROVED' ? 'approved' :
    'neutral'

  return (
    <div className="saas-card">
      <div className="saas-card-header">
        <div className="saas-card-title">
          <span className="saas-card-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
          </span>
          <span>{t.saasApplications || 'Aplicaciones SaaS'}</span>
          <span className="saas-card-count">{apps.length}</span>
        </div>
        <a
          href={jumpcloudUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="saas-card-cta"
        >
          JumpCloud
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
            <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
        </a>
      </div>

      <div className="saas-card-body">
        {apps.length === 0 ? (
          <div className="saas-empty">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
            <div className="saas-empty-title">{t.noSaaSAppsDetected || 'No SaaS applications detected'}</div>
            <div className="saas-empty-sub">{t.saaSAppsWillAppear || 'Applications will appear as users access them'}</div>
          </div>
        ) : (
          <>
            {/* KPI strip — single row, 4 stats. Large primary "approved %"
                anchors the visual; the smaller numbers communicate the
                detail in proportion. */}
            <div className="saas-kpis">
              <div className="saas-kpi saas-kpi--primary">
                <div className="saas-kpi-value">{approvedRate}%</div>
                <div className="saas-kpi-label">aprobado</div>
              </div>
              <div className="saas-kpi-divider" aria-hidden="true" />
              <div className="saas-kpi">
                <div className="saas-kpi-value">{apps.length}</div>
                <div className="saas-kpi-label">total</div>
              </div>
              <div className="saas-kpi saas-kpi--new" data-zero={counts.new === 0}>
                <div className="saas-kpi-value">{counts.new}</div>
                <div className="saas-kpi-label">nuevas</div>
              </div>
              <div className="saas-kpi saas-kpi--review" data-zero={counts.unapproved === 0}>
                <div className="saas-kpi-value">{counts.unapproved}</div>
                <div className="saas-kpi-label">revisar</div>
              </div>
            </div>

            {needsAttention > 0 && (
              <div className="saas-alert">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                <span>
                  <strong>{needsAttention}</strong>{' '}
                  {needsAttention === 1 ? 'aplicación requiere' : 'aplicaciones requieren'} revisión
                </span>
              </div>
            )}

            {/* Logo grid — 6 columns desktop, 4 tablet, 3 mobile.
                Each tile is borderless when neutral, status-colored
                when not, with a tiny corner dot for at-a-glance status. */}
            <div className="saas-grid">
              {visibleApps.map((app, i) => {
                const logoUrl = getLogoUrl(app)
                const showFallback = !logoUrl || failedLogos.has(app.name)
                const mod = statusMod(app.status)
                return (
                  <a
                    key={i}
                    href={jumpcloudUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`saas-tile saas-tile--${mod}`}
                    title={`${app.name} · ${getAppCategory(app)}`}
                  >
                    <div className="saas-tile-logo">
                      {showFallback ? (
                        <span className="saas-tile-initials">{getInitials(app.name)}</span>
                      ) : (
                        <img
                          src={logoUrl!}
                          alt=""
                          loading="lazy"
                          onError={() => setFailedLogos(prev => new Set(prev).add(app.name))}
                        />
                      )}
                      {mod !== 'neutral' && mod !== 'approved' && (
                        <span className={`saas-tile-dot saas-tile-dot--${mod}`} aria-hidden="true" />
                      )}
                    </div>
                    <div className="saas-tile-name">{app.name}</div>
                    <div className="saas-tile-cat">{getAppCategory(app)}</div>
                  </a>
                )
              })}
            </div>

            {apps.length > visibleApps.length && (
              <a
                href={jumpcloudUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="saas-view-all"
              >
                {`Ver todas ${apps.length} aplicaciones`}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </a>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * RecentAlertsSummary — dashboard-row replacement for the full alerts
 * feed. Shows: total count, severity breakdown bars, top-5 most recent
 * items with timestamps and source pills, and a single "Ver todas →"
 * CTA wired to onSeeAll() (which the parent routes to the dedicated
 * alerts section). The whole card is keyboard-activatable as a button.
 */
function RecentAlertsSummary({
  alerts,
  onSeeAll,
}: {
  alerts: UnifiedAlert[]
  onSeeAll: () => void
}) {
  const { language } = useLanguage()
  const total = alerts.length

  // Severity histogram — "informational" rolls into "low" so the
  // bar chart stays readable at four levels.
  const sevBuckets = {
    critical: alerts.filter(a => a.severity === 'critical').length,
    high:     alerts.filter(a => a.severity === 'high').length,
    medium:   alerts.filter(a => a.severity === 'medium').length,
    low:      alerts.filter(a => a.severity === 'low' || a.severity === 'informational').length,
  }
  const maxBar = Math.max(1, ...Object.values(sevBuckets))

  // Most recent 5 — alerts already arrive sorted desc from the API,
  // but be defensive in case the API ever changes ordering.
  const recent = [...alerts]
    .sort((a, b) => +new Date(b.timestamp) - +new Date(a.timestamp))
    .slice(0, 5)

  const formatRelative = (iso: string) => {
    const d = (Date.now() - +new Date(iso)) / 1000
    if (d < 60) return language === 'es' ? 'ahora' : 'now'
    if (d < 3600) return `${Math.floor(d / 60)}m`
    if (d < 86400) return `${Math.floor(d / 3600)}h`
    const days = Math.floor(d / 86400)
    return `${days}d`
  }

  return (
    <section className="alerts-summary" role="region" aria-label="Recent alerts summary">
      <header className="alerts-summary-header">
        <div className="alerts-summary-title">
          <span className="alerts-summary-icon" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </span>
          {language === 'es' ? 'Alertas recientes' : 'Recent alerts'}
          <span className="alerts-summary-count">{total}</span>
        </div>
        <button type="button" className="alerts-summary-cta" onClick={onSeeAll}>
          {language === 'es' ? 'Ver todas' : 'See all'}
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </header>

      <div className="alerts-summary-body">
        {/* Severity histogram — left column, ~38% width */}
        <div className="alerts-sev">
          <div className="alerts-sev-label">
            {language === 'es' ? 'Por severidad' : 'By severity'}
          </div>
          <div className="alerts-sev-bars">
            {(['critical', 'high', 'medium', 'low'] as const).map(level => {
              const value = sevBuckets[level]
              const pct = (value / maxBar) * 100
              const labels = language === 'es'
                ? { critical: 'Crítica', high: 'Alta', medium: 'Media', low: 'Baja' }
                : { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' }
              return (
                <button
                  key={level}
                  type="button"
                  className={`alerts-sev-bar alerts-sev-bar--${level}`}
                  data-zero={value === 0}
                  onClick={onSeeAll}
                  title={`${value} ${labels[level]}`}
                >
                  <span className="alerts-sev-bar-label">{labels[level]}</span>
                  <span className="alerts-sev-bar-track">
                    <span className="alerts-sev-bar-fill" style={{ width: `${pct}%` }} />
                  </span>
                  <span className="alerts-sev-bar-value">{value}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Recent feed — right column, ~62% width */}
        <div className="alerts-recent">
          <div className="alerts-recent-label">
            {language === 'es' ? 'Más recientes' : 'Most recent'}
          </div>
          <ul className="alerts-recent-list">
            {recent.map(a => (
              <li key={a.id}>
                <button
                  type="button"
                  className="alerts-recent-row"
                  onClick={onSeeAll}
                >
                  <span className={`alerts-recent-dot alerts-recent-dot--${a.severity}`} aria-hidden="true" />
                  <span className="alerts-recent-text">{a.title}</span>
                  <span className={`alerts-recent-source alerts-recent-source--${a.source}`}>
                    {a.source === 'crowdstrike' ? 'EDR' : 'IAM'}
                  </span>
                  <span className="alerts-recent-time">{formatRelative(a.timestamp)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

function ComplianceCard({ compliance }: { compliance: DeviceCompliance | null }) {
  const { t } = useLanguage()
  const [selectedPolicy, setSelectedPolicy] = useState<CompliancePolicy | null>(null)

  // Use real data from API only - no mock data
  const policies = compliance?.policies || []
  const hasData = policies.length > 0

  return (
    <>
      <div className="card-v2">
        <div className="card-v2-header">
          <div className="card-v2-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>
            {t.policyCompliance}
          </div>
          <span className="card-v2-subtitle">{t.jumpCloudPolicies}</span>
        </div>
        <div className="card-v2-content">
          {!hasData ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ margin: '0 auto 8px', opacity: 0.5 }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/>
              </svg>
              <div>{'No policies configured'}</div>
            </div>
          ) : (
            <div className="compliance-grid">
              {policies.map((policy, i) => {
                const total = policy.compliant + policy.nonCompliant + (policy.unknown || 0)
                const rate = total > 0 ? Math.round((policy.compliant / total) * 100) : 0
                const status = rate >= 90 ? 'success' : rate >= 70 ? 'warning' : 'danger'
                return (
                  <div
                    key={i}
                    className="compliance-item compliance-item-clickable"
                    onClick={() => setSelectedPolicy(policy)}
                    style={{ cursor: 'pointer' }}
                    title="Click para ver dispositivos"
                  >
                    <div className={`compliance-icon ${status}`}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                    </div>
                    <div className="compliance-info">
                      <div className="compliance-name">{policy.name}</div>
                      <div className="compliance-desc">{policy.nonCompliant} {t.notCompliant}</div>
                    </div>
                    <div className="compliance-value">
                      <div className={`compliance-number ${status}`}>{policy.compliant}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal de dispositivos por política */}
      {selectedPolicy && (
        <CompliancePolicyModal
          policy={selectedPolicy}
          onClose={() => setSelectedPolicy(null)}
        />
      )}
    </>
  )
}

// Modal para mostrar dispositivos de una política de compliance
function CompliancePolicyModal({ policy, onClose }: { policy: CompliancePolicy; onClose: () => void }) {
  const { t } = useLanguage()
  const [filter, setFilter] = useState<'all' | 'compliant' | 'non_compliant'>('all')

  const devices = policy.devices || []
  const filteredDevices = filter === 'all'
    ? devices
    : devices.filter(d => d.status === filter)

  const total = devices.length
  const compliantCount = devices.filter(d => d.status === 'compliant').length
  const nonCompliantCount = devices.filter(d => d.status === 'non_compliant').length
  const unknownCount = devices.filter(d => d.status === 'unknown').length
  const complianceRate = total > 0 ? Math.round((compliantCount / total) * 100) : 0

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': return 'var(--success)'
      case 'non_compliant': return 'var(--danger)'
      default: return 'var(--text-muted)'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'compliant': return 'Compliant'
      case 'non_compliant': return t.notCompliant || 'Not Compliant'
      default: return 'Unknown'
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3 className="modal-title">{policy.name}</h3>
            <p className="modal-subtitle">{policy.description}</p>
          </div>
          <button className="modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Summary stats */}
        <div className="compliance-modal-stats">
          <div className="compliance-stat">
            <div className="compliance-stat-value" style={{ color: 'var(--success)' }}>{compliantCount}</div>
            <div className="compliance-stat-label">Compliant</div>
          </div>
          <div className="compliance-stat">
            <div className="compliance-stat-value" style={{ color: 'var(--danger)' }}>{nonCompliantCount}</div>
            <div className="compliance-stat-label">{t.notCompliant || 'Not Compliant'}</div>
          </div>
          {unknownCount > 0 && (
            <div className="compliance-stat">
              <div className="compliance-stat-value" style={{ color: 'var(--text-muted)' }}>{unknownCount}</div>
              <div className="compliance-stat-label">Unknown</div>
            </div>
          )}
          <div className="compliance-stat">
            <div className="compliance-stat-value">{complianceRate}%</div>
            <div className="compliance-stat-label">Compliance Rate</div>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="compliance-modal-filters">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({total})
          </button>
          <button
            className={`filter-tab ${filter === 'compliant' ? 'active' : ''}`}
            onClick={() => setFilter('compliant')}
          >
            Compliant ({compliantCount})
          </button>
          <button
            className={`filter-tab ${filter === 'non_compliant' ? 'active' : ''}`}
            onClick={() => setFilter('non_compliant')}
          >
            {t.notCompliant || 'Not Compliant'} ({nonCompliantCount})
          </button>
        </div>

        {/* Device list */}
        <div className="modal-body">
          {filteredDevices.length === 0 ? (
            <div className="empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.5 }}>
                <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
              </svg>
              <p>No devices found</p>
            </div>
          ) : (
            <div className="device-list">
              {filteredDevices.map((device, idx) => (
                <div key={device.deviceId || idx} className="device-item">
                  <div className="device-icon">
                    <PlatformIcon platform={device.platform} />
                  </div>
                  <div className="device-info">
                    <div className="device-name">{device.deviceName}</div>
                    <div className="device-platform">{device.platform}</div>
                  </div>
                  <div
                    className="device-status"
                    style={{
                      color: getStatusColor(device.status),
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {device.status === 'compliant' ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                    ) : device.status === 'non_compliant' ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                    )}
                    {getStatusLabel(device.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function TimelineCard({ timeline }: { timeline: ScoreTimeline | null }) {
  const { t } = useLanguage()
  const hasEvents = timeline && timeline.events && timeline.events.length > 0

  return (
    <div className="card-v2">
      <div className="card-v2-header">
        <div className="card-v2-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
          {t.securityScoreEvolution}
        </div>
        <span className="card-v2-subtitle">{t.lastDaysWithEvents}</span>
      </div>
      <div className="card-v2-content">
        {!hasEvents ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 16px', color: 'var(--text-muted)' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.4, marginBottom: '12px' }}>
              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
            <p style={{ fontSize: '14px', margin: 0 }}>{t.noSecurityEventsRecorded}</p>
            <p style={{ fontSize: '12px', margin: '4px 0 0', opacity: 0.7 }}>{t.scoreHistoryWillAppear}</p>
          </div>
        ) : (
          <>
            <div className="timeline-chart">
              <svg viewBox="0 0 800 150" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
                <line x1="0" y1="30" x2="800" y2="30" stroke="var(--border-subtle)" strokeWidth="1" />
                <line x1="0" y1="75" x2="800" y2="75" stroke="var(--border-subtle)" strokeWidth="1" />
                <line x1="0" y1="120" x2="800" y2="120" stroke="var(--border-subtle)" strokeWidth="1" />
                <path d="M0,100 C100,95 150,90 200,85 C250,90 300,80 350,75 C400,70 450,65 500,60 C550,55 600,50 650,45 C700,40 750,35 800,30" fill="none" stroke="url(#timelineGrad)" strokeWidth="3" />
                <defs>
                  <linearGradient id="timelineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#f85149" />
                    <stop offset="50%" stopColor="#ffa94d" />
                    <stop offset="100%" stopColor="#3fb950" />
                  </linearGradient>
                </defs>
                <circle cx="200" cy="85" r="6" fill="#f85149" />
                <circle cx="350" cy="75" r="6" fill="#ffa94d" />
                <circle cx="500" cy="60" r="6" fill="#3fb950" />
                <circle cx="650" cy="45" r="6" fill="#3fb950" />
              </svg>
            </div>
            <div style={{ display: 'flex', gap: '16px', marginTop: '16px', flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-muted)' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f85149' }} />{t.ransomwareBlocked}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-muted)' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ffa94d' }} />+2 {t.cvesPatched}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-muted)' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3fb950' }} />{t.eventMFAEnabled}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// On-demand section wrappers (fetch data when section becomes active)
function SoftwareSection({ tenantSlug, totalDevices }: { tenantSlug: string; totalDevices?: number }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [softwareData, setSoftwareData] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.getSoftwareInventory(tenantSlug)
      .then(res => {
        if (res.success && res.data) {
          const d = res.data as any
          // Worker returns { success, data: [...inventory...] } — unwrap inner data
          setSoftwareData(Array.isArray(d.data) ? d.data : [])
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [tenantSlug])

  return <SoftwareInventory software={softwareData} isLoading={loading} totalDevices={totalDevices} />
}

function ExtensionsSection({ tenantSlug }: { tenantSlug: string }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [extData, setExtData] = useState<any[]>([])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [extSummary, setExtSummary] = useState<any>(undefined)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.getBrowserExtensions(tenantSlug)
      .then(res => {
        if (res.success && res.data) {
          const d = res.data as any
          // Worker returns { success, data: [...extensions...], summary: {...} } — unwrap inner data
          setExtData(Array.isArray(d.data) ? d.data : [])
          setExtSummary(d.summary)
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [tenantSlug])

  if (loading) {
    return <div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />
  }
  return <BrowserExtensionsAudit extensions={extData} summary={extSummary} />
}

function USBSection({ tenantSlug }: { tenantSlug: string }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [usbData, setUsbData] = useState<any[]>([])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [usbSummary, setUsbSummary] = useState<any>(undefined)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.getUSBDevices(tenantSlug)
      .then(res => {
        if (res.success && res.data) {
          const d = res.data as any
          // Worker returns { success, data: [...devices...], summary: {...} } — unwrap inner data
          setUsbData(Array.isArray(d.data) ? d.data : [])
          setUsbSummary(d.summary)
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [tenantSlug])

  if (loading) {
    return <div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />
  }
  return <USBMonitoring devices={usbData} summary={usbSummary} />
}

function ComplianceSection({ tenantSlug }: { tenantSlug: string }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [policies, setPolicies] = useState<any[]>([])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [assetGrid, setAssetGrid] = useState<any[]>([])
  const [totalDevices, setTotalDevices] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.getPolicyCompliance(tenantSlug)
      .then(res => {
        if (res.success && res.data) {
          const d = res.data as any
          // Worker returns { success, data: { policies, totalDevices } } — unwrap inner data
          const payload = d.data || {}
          setPolicies(payload.policies || [])
          setAssetGrid(payload.assetGrid || [])
          setTotalDevices(payload.totalDevices || 0)
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [tenantSlug])

  if (loading) {
    return <div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />
  }
  return (
    <>
      <PolicyComplianceMatrix policies={policies} totalDevices={totalDevices} />
      <AssetComplianceGrid devices={assetGrid} />
    </>
  )
}

function AccessSection({ tenantSlug }: { tenantSlug: string }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [graphData, setGraphData] = useState<{ nodes: any[]; edges: any[]; summary?: any }>({ nodes: [], edges: [] })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.getAccessGraph(tenantSlug)
      .then(res => {
        if (res.success && res.data) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const d = res.data as any
          // Worker returns { success, data: { nodes, edges, groups }, summary } — unwrap inner data
          const payload = d.data || {}
          setGraphData({
            nodes: payload.nodes || [],
            edges: payload.edges || [],
            summary: d.summary,
          })
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [tenantSlug])

  if (loading) {
    return <div className="card-v2 h-48 animate-pulse" style={{ background: 'var(--bg-card)' }} />
  }
  return <AccessGraph nodes={graphData.nodes} edges={graphData.edges} summary={graphData.summary} />
}

function ThreatIntelSection({ tenantSlug, onDarkWebClick }: { tenantSlug: string; onDarkWebClick?: () => void }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [reportData, setReportData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    api.getSocRadarReport(tenantSlug)
      .then(res => {
        if (res.success && res.data) {
          const d = res.data as any
          setReportData(d.data || d)
        }
      })
      .catch(() => setError('Error loading report'))
      .finally(() => setLoading(false))
  }, [tenantSlug])

  return <SocRadarReport data={reportData} loading={loading} error={error} onDarkWebClick={onDarkWebClick} />
}

function ExposureSection({ tenantSlug }: { tenantSlug: string }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [exposureData, setExposureData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    api.getExposureAssets(tenantSlug)
      .then(res => {
        if (res.success && res.data) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const d = res.data as any
          // Worker returns { success, data: exposureSummary } — unwrap inner data
          const payload = d.data || d
          const data = { ...payload, assets: Array.isArray(payload.assets) ? payload.assets : [] }
          setExposureData(data)
        }
      })
      .catch(err => {
        console.error('ExposureSection error:', err)
        setError(err?.message || 'Error loading exposure data')
      })
      .finally(() => setLoading(false))
  }, [tenantSlug])

  if (error) {
    return (
      <div className="card-v2 p-6 text-center">
        <p className="text-[#f85149] mb-2">Error loading exposure data</p>
        <p className="text-sm text-[#8b949e]">{error}</p>
      </div>
    )
  }

  return <ExposureMap data={exposureData} loading={loading} />
}

function SocRadarIncidentsSection({ tenantSlug }: { tenantSlug: string }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [incData, setIncData] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.getSocRadarIncidents(tenantSlug)
      .then(res => {
        if (res.success && res.data) {
          const d = res.data as any
          setIncData(d.data || d)
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [tenantSlug])

  return <SocRadarIncidents data={incData} loading={loading} />
}

function SocRadarASMSection({ tenantSlug }: { tenantSlug: string }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [asmData, setAsmData] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.getSocRadarASM(tenantSlug)
      .then(res => {
        if (res.success && res.data) {
          const d = res.data as any
          setAsmData(d.data || d)
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [tenantSlug])

  return <SocRadarASM data={asmData} loading={loading} />
}

function DarkWebSection({ tenantSlug }: { tenantSlug: string }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [dwData, setDwData] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.getSocRadarDarkWeb(tenantSlug)
      .then(res => {
        if (res.success && res.data) {
          const d = res.data as any
          setDwData(d.data || d)
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [tenantSlug])

  return <DarkWebMonitoring data={dwData} loading={loading} />
}



// ============================================
// Dedicated Users Section
// ============================================
// Preset filters for the users section
type UserFilterPreset =
  | 'all'
  | 'active'
  | 'suspended'
  | 'noMfa'
  | 'passwordExpiring'
  | 'locked'
  | 'newUsers'
  | 'inactive'
  | 'highRisk'

// Derive MFA method from the raw JumpCloud user object.
// Returns 'none' when no MFA method is configured.
function getMfaType(user: JumpCloudUser): 'totp' | 'webauthn' | 'push' | 'sms' | 'none' {
  const methods = user.mfa?.methods || []
  if (methods.includes('webauthn')) return 'webauthn'
  if (methods.includes('push')) return 'push'
  if (methods.includes('totp') || user.totp_enabled) return 'totp'
  if (methods.includes('sms')) return 'sms'
  if (user.mfa?.configured || user.enable_user_portal_multifactor) return 'totp'
  return 'none'
}

// RFC 4180 + CSV-injection-safe cell encoder.
// Prefixes cells starting with =, +, -, @, tab, or CR with a single quote
// to prevent formula injection when opened in Excel/Sheets.
function csvCell(value: unknown): string {
  const raw = value == null ? '' : String(value)
  const needsPrefix = /^[=+\-@\t\r]/.test(raw)
  const safe = needsPrefix ? `'${raw}` : raw
  return `"${safe.replace(/"/g, '""')}"`
}

function UsersDedicatedSection({ data, mfaStatus, onUserClick }: {
  data: DashboardData | null;
  mfaStatus: MFAStatus | null;
  onUserClick?: (userId: string) => void;
}) {
  const { t } = useLanguage()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterPreset, setFilterPreset] = useState<UserFilterPreset>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const PAGE_SIZE = 20

  const users = data?.users || []

  // Reset pagination when the visible set changes (new filter or new search).
  // Prevents being stuck on page 5 after filtering down to 3 results.
  useEffect(() => {
    setCurrentPage(1)
  }, [filterPreset, searchTerm])

  // Build set of user IDs without MFA for quick lookup
  const noMfaIds = new Set((mfaStatus?.usersWithoutMFA || []).map(u => u.id))
  const noMfaEmails = new Set((mfaStatus?.usersWithoutMFA || []).map(u => u.email?.toLowerCase()))

  const now = Date.now()
  const DAY = 24 * 60 * 60 * 1000

  const isUserWithoutMFA = (user: JumpCloudUser) => {
    return noMfaIds.has(user.id) || (!!user.email && noMfaEmails.has(user.email.toLowerCase()))
  }

  const isPasswordExpiringSoon = (user: JumpCloudUser) => {
    if (!user.password_expiration_date || user.password_never_expires) return false
    try {
      const diff = new Date(user.password_expiration_date).getTime() - now
      return diff > 0 && diff <= 14 * DAY
    } catch { return false }
  }

  const isNewUser = (user: JumpCloudUser) => {
    if (!user.created) return false
    try { return (now - new Date(user.created).getTime()) <= 7 * DAY } catch { return false }
  }

  const isInactive = (user: JumpCloudUser) => {
    if (!user.lastLogin) return true // never logged in
    try { return (now - new Date(user.lastLogin).getTime()) > 90 * DAY } catch { return false }
  }

  // Apply search + preset filter
  const filteredUsers = users.filter(u => {
    // Preset filter
    switch (filterPreset) {
      case 'active': if (!(u.activated && !u.suspended)) return false; break
      case 'suspended': if (!u.suspended) return false; break
      case 'noMfa': if (!isUserWithoutMFA(u)) return false; break
      case 'passwordExpiring': if (!isPasswordExpiringSoon(u)) return false; break
      case 'locked': if (!u.account_locked) return false; break
      case 'newUsers': if (!isNewUser(u)) return false; break
      case 'inactive': if (!isInactive(u)) return false; break
      case 'highRisk': {
        const hasMFA = !isUserWithoutMFA(u)
        const r = calculateUserRisk({ user: u, hasMFA, mfaType: getMfaType(u) })
        if (r.level !== 'high') return false
        break
      }
      case 'all': default: break
    }
    // Search filter (sanitized via toLowerCase, no regex)
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    const fullName = `${u.firstname || ''} ${u.lastname || ''}`.toLowerCase()
    return fullName.includes(term)
      || (u.email || '').toLowerCase().includes(term)
      || (u.username || '').toLowerCase().includes(term)
  })

  // Summary counts (from full set, independent of filters)
  const totalUsers = users.length
  const activeUsers = users.filter(u => u.activated && !u.suspended).length
  const suspendedUsers = users.filter(u => u.suspended).length
  const noMfaCount = mfaStatus?.usersWithoutMFA?.length ?? 0

  // Pagination slice
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE))
  const safePage = Math.min(Math.max(1, currentPage), totalPages)
  const pagedUsers = filteredUsers.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  // Recent Activity rollup — calculated from current snapshot, no history required.
  // "newLast7d" = users.created within 7 days
  // "newLast30d" = users.created within 30 days
  // "suspendedActive" = users flagged suspended (still present in org)
  // "highRiskCount" = users whose calculateUserRisk().level === 'high'
  const DAY_MS = 86_400_000
  const nowMs = Date.now()
  const activitySummary = {
    newLast7d: users.filter(u => {
      if (!u.created) return false
      try { return (nowMs - new Date(u.created).getTime()) <= 7 * DAY_MS } catch { return false }
    }).length,
    newLast30d: users.filter(u => {
      if (!u.created) return false
      try { return (nowMs - new Date(u.created).getTime()) <= 30 * DAY_MS } catch { return false }
    }).length,
    suspendedActive: suspendedUsers,
    highRiskCount: users.filter(u => {
      const hasMFA = !isUserWithoutMFA(u)
      return calculateUserRisk({ user: u, hasMFA, mfaType: getMfaType(u) }).level === 'high'
    }).length,
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return t.usersNever
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    } catch { return dateStr }
  }

  const mfaTypeLabel = (type: ReturnType<typeof getMfaType>) => {
    switch (type) {
      case 'totp': return t.usersMFATotp
      case 'webauthn': return t.usersMFAWebauthn
      case 'push': return t.usersMFAPush
      case 'sms': return t.usersMFASms
      case 'none': return t.usersMFANone
    }
  }

  const handleExportCSV = () => {
    const headers = [
      t.usersName, t.usersEmail, t.usersStatus, t.usersRisk, 'Risk Score',
      t.usersMFA, t.usersMFAType, t.usersLastLogin, t.usersPasswordStatus
    ]
    const rows = filteredUsers.map(user => {
      const hasMFA = !isUserWithoutMFA(user)
      const isActive = user.activated && !user.suspended
      const mfaType = getMfaType(user)
      const risk = calculateUserRisk({ user, hasMFA, mfaType })
      const riskLabel = risk.level === 'high' ? t.userRiskHigh
        : risk.level === 'medium' ? t.userRiskMedium
        : t.userRiskLow
      const name = user.firstname || user.lastname
        ? `${user.firstname || ''} ${user.lastname || ''}`.trim()
        : user.username
      return [
        name,
        user.email || '',
        isActive ? t.usersActive : t.usersSuspended,
        riskLabel,
        String(risk.score),
        hasMFA ? t.usersYes : t.usersNo,
        mfaTypeLabel(mfaType),
        user.lastLogin || '',
        user.password_expired ? t.usersExpired : t.usersValid,
      ]
    })
    const csv = [headers, ...rows]
      .map(row => row.map(csvCell).join(','))
      .join('\r\n')
    // Prepend BOM so Excel detects UTF-8 correctly
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    const stamp = new Date().toISOString().split('T')[0]
    link.download = `jarix_users_${stamp}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const presets: Array<{ key: UserFilterPreset; label: string }> = [
    { key: 'all', label: t.usersFilterAll },
    { key: 'highRisk', label: t.usersFilterHighRisk },
    { key: 'active', label: t.usersFilterActive },
    { key: 'suspended', label: t.usersFilterSuspended },
    { key: 'noMfa', label: t.usersFilterNoMFA },
    { key: 'passwordExpiring', label: t.usersFilterPasswordExpiring },
    { key: 'locked', label: t.usersFilterLocked },
    { key: 'newUsers', label: t.usersFilterNewUsers },
    { key: 'inactive', label: t.usersFilterInactive },
  ]

  return (
    <div className="card-v2">
      <div className="card-v2-header">
        <div className="card-v2-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          {t.jumpcloudUsersTitle}
          <span className="card-v2-badge">{totalUsers}</span>
        </div>
        <span className="card-v2-subtitle">JumpCloud IAM</span>
      </div>
      <div className="card-v2-content">
        {/* Summary cards */}
        <div className="dedicated-summary-grid">
          <div className="dedicated-summary-card">
            <div className="dedicated-summary-value">{totalUsers}</div>
            <div className="dedicated-summary-label">{t.usersSummaryTotal}</div>
          </div>
          <div className="dedicated-summary-card success">
            <div className="dedicated-summary-value">{activeUsers}</div>
            <div className="dedicated-summary-label">{t.usersSummaryActive}</div>
          </div>
          <div className="dedicated-summary-card warning">
            <div className="dedicated-summary-value">{suspendedUsers}</div>
            <div className="dedicated-summary-label">{t.usersSummarySuspended}</div>
          </div>
          <div className="dedicated-summary-card danger">
            <div className="dedicated-summary-value">{noMfaCount}</div>
            <div className="dedicated-summary-label">{t.usersSummaryNoMFA}</div>
          </div>
        </div>

        {/* Filter chips */}
        <div className="dedicated-filter-chips">
          {presets.map(p => (
            <button
              key={p.key}
              type="button"
              className={`filter-chip ${filterPreset === p.key ? 'active' : ''}`}
              onClick={() => setFilterPreset(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Recent Activity rollup (Phase D) */}
        <div className="user-activity-rollup">
          <div className="user-activity-title">{t.activityRecentTitle}</div>
          <div className="user-activity-cells">
            <button
              type="button"
              className="user-activity-cell"
              onClick={() => setFilterPreset('newUsers')}
              title={t.activityNew7dHint}
            >
              <span className="user-activity-value">{activitySummary.newLast7d}</span>
              <span className="user-activity-label">{t.activityNew7d}</span>
            </button>
            <div className="user-activity-cell" aria-hidden="true">
              <span className="user-activity-value">{activitySummary.newLast30d}</span>
              <span className="user-activity-label">{t.activityNew30d}</span>
            </div>
            <button
              type="button"
              className="user-activity-cell"
              onClick={() => setFilterPreset('suspended')}
              title={t.activitySuspendedHint}
            >
              <span className="user-activity-value warning">{activitySummary.suspendedActive}</span>
              <span className="user-activity-label">{t.activitySuspended}</span>
            </button>
            <button
              type="button"
              className="user-activity-cell"
              onClick={() => setFilterPreset('highRisk')}
              title={t.activityHighRiskHint}
            >
              <span className="user-activity-value danger">{activitySummary.highRiskCount}</span>
              <span className="user-activity-label">{t.activityHighRisk}</span>
            </button>
          </div>
        </div>

        {/* Search + Export */}
        <div className="dedicated-toolbar">
          <div className="dedicated-search-wrapper">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
            </svg>
            <input
              type="text"
              className="dedicated-search-input"
              placeholder={t.usersSearchPlaceholder}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="dedicated-export-btn"
            onClick={handleExportCSV}
            disabled={filteredUsers.length === 0}
            title={t.usersExportCSV}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {t.usersExportCSV}
          </button>
        </div>

        {/* Result count */}
        <div className="dedicated-result-count">
          {t.usersShowingCount
            .replace('{visible}', String(filteredUsers.length))
            .replace('{total}', String(totalUsers))}
        </div>

        {/* Users table */}
        <div className="dedicated-section-table-wrapper">
          <table className="dedicated-section-table">
            <thead>
              <tr>
                <th>{t.usersName}</th>
                <th>{t.usersEmail}</th>
                <th>{t.usersStatus}</th>
                <th>{t.usersRisk}</th>
                <th>{t.usersMFA}</th>
                <th>{t.usersMFAType}</th>
                <th>{t.usersLastLogin}</th>
                <th>{t.usersPasswordStatus}</th>
              </tr>
            </thead>
            <tbody>
              {pagedUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-muted" style={{ textAlign: 'center', padding: '24px' }}>
                    {t.usersNoResults}
                  </td>
                </tr>
              ) : pagedUsers.map(user => {
                const hasMFA = !isUserWithoutMFA(user)
                const isActive = user.activated && !user.suspended
                const mfaType = getMfaType(user)
                const risk = calculateUserRisk({ user, hasMFA, mfaType })
                const riskLabel = risk.level === 'high' ? t.userRiskHigh
                  : risk.level === 'medium' ? t.userRiskMedium
                  : t.userRiskLow
                const clickable = !!onUserClick
                const handleRowClick = () => { if (onUserClick) onUserClick(user.id) }
                const handleKey = (e: React.KeyboardEvent) => {
                  if (!onUserClick) return
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onUserClick(user.id) }
                }
                return (
                  <tr
                    key={user.id}
                    className={`${!hasMFA ? 'row-highlight-danger' : ''} ${clickable ? 'row-clickable' : ''}`.trim()}
                    onClick={clickable ? handleRowClick : undefined}
                    onKeyDown={clickable ? handleKey : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    role={clickable ? 'button' : undefined}
                    aria-label={clickable ? t.usersViewDetails : undefined}
                  >
                    <td className="font-medium">
                      {user.firstname || user.lastname
                        ? `${user.firstname || ''} ${user.lastname || ''}`.trim()
                        : user.username}
                    </td>
                    <td className="text-muted">{user.email}</td>
                    <td>
                      <span className={`status-badge ${isActive ? 'success' : 'warning'}`}>
                        {isActive ? t.usersActive : t.usersSuspended}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge risk-${risk.level}`} title={`${risk.score}/100`}>
                        {riskLabel}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${hasMFA ? 'success' : 'critical'}`}>
                        {hasMFA ? t.usersYes : t.usersNo}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge-subtle mfa-${mfaType}`}>
                        {mfaTypeLabel(mfaType)}
                      </span>
                    </td>
                    <td className="text-muted">{formatDate(user.lastLogin)}</td>
                    <td>
                      <span className={`status-badge-subtle ${user.password_expired ? 'expired' : 'valid'}`}>
                        {user.password_expired ? t.usersExpired : t.usersValid}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination controls */}
        {filteredUsers.length > PAGE_SIZE && (
          <div className="users-pagination">
            <button
              type="button"
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              aria-label={t.paginationPrev}
            >
              ← {t.paginationPrev}
            </button>
            <span className="pagination-info">
              {t.paginationPageOf
                .replace('{page}', String(safePage))
                .replace('{total}', String(totalPages))}
            </span>
            <button
              type="button"
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              aria-label={t.paginationNext}
            >
              {t.paginationNext} →
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================
// Dedicated Devices Section
// ============================================
function DevicesDedicatedSection({ data, onDeviceClick }: {
  data: DashboardData | null;
  onDeviceClick: (aid: string) => void;
}) {
  const { t } = useLanguage()
  const [searchTerm, setSearchTerm] = useState('')
  const [sourceFilter, setSourceFilter] = useState<'all' | 'crowdstrike' | 'jumpcloud'>('all')
  const [osFilter, setOsFilter] = useState<'all' | 'windows' | 'mac' | 'linux'>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'online' | 'offline'>('all')

  const devices = data?.devices || []

  // Determine online/offline based on last_seen (within 30 min = online)
  const isOnline = (lastSeen: string) => {
    try {
      const diff = Date.now() - new Date(lastSeen).getTime()
      return diff < 30 * 60 * 1000 // 30 minutes
    } catch { return false }
  }

  // Apply all filters
  const filteredDevices = devices.filter(d => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      const matchHost = d.hostname?.toLowerCase().includes(term)
      const matchIP = d.local_ip?.toLowerCase().includes(term) || d.external_ip?.toLowerCase().includes(term)
      if (!matchHost && !matchIP) return false
    }
    if (sourceFilter !== 'all' && d.source !== sourceFilter && d.source !== 'both') return false
    if (osFilter !== 'all') {
      const p = (d.platform_name || '').toLowerCase()
      if (osFilter === 'windows' && !p.includes('windows')) return false
      if (osFilter === 'mac' && !p.includes('mac') && !p.includes('darwin')) return false
      if (osFilter === 'linux' && !p.includes('linux')) return false
    }
    if (statusFilter !== 'all') {
      const online = isOnline(d.last_seen)
      if (statusFilter === 'online' && !online) return false
      if (statusFilter === 'offline' && online) return false
    }
    return true
  })

  const totalDevices = devices.length
  const onlineDevices = devices.filter(d => isOnline(d.last_seen)).length
  const offlineDevices = totalDevices - onlineDevices
  const jcOnlyDevices = devices.filter(d => d.source === 'jumpcloud').length

  // Platform breakdown
  const platformCounts: Record<string, number> = {}
  for (const d of devices) {
    const p = d.platform_name || 'Unknown'
    platformCounts[p] = (platformCounts[p] || 0) + 1
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '--'
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' +
             d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    } catch { return dateStr }
  }

  const getSourceBadge = (source?: string) => {
    switch (source) {
      case 'crowdstrike': return <span className="source-badge cs">CrowdStrike</span>
      case 'both': return <span className="source-badge cs">CrowdStrike</span>
      case 'jumpcloud': return <span className="source-badge jc-only">No EDR</span>
      default: return <span className="source-badge">--</span>
    }
  }

  return (
    <div className="card-v2">
      <div className="card-v2-header">
        <div className="card-v2-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8m-4-4v4"/>
          </svg>
          {t.devicesTitle}
          <span className="card-v2-badge">{totalDevices}</span>
        </div>
        <span className="card-v2-subtitle">CrowdStrike + JumpCloud</span>
      </div>
      <div className="card-v2-content">
        {/* Summary cards */}
        <div className="dedicated-summary-grid">
          <div className="dedicated-summary-card">
            <div className="dedicated-summary-value">{totalDevices}</div>
            <div className="dedicated-summary-label">{t.devicesSummaryTotal}</div>
          </div>
          <div className="dedicated-summary-card success">
            <div className="dedicated-summary-value">{onlineDevices}</div>
            <div className="dedicated-summary-label">{t.devicesSummaryOnline}</div>
          </div>
          <div className="dedicated-summary-card warning">
            <div className="dedicated-summary-value">{offlineDevices}</div>
            <div className="dedicated-summary-label">{t.devicesSummaryOffline}</div>
          </div>
          {jcOnlyDevices > 0 && (
            <div className="dedicated-summary-card" style={{ borderColor: 'var(--status-critical)' }}>
              <div className="dedicated-summary-value" style={{ color: 'var(--status-critical)' }}>{jcOnlyDevices}</div>
              <div className="dedicated-summary-label">No EDR</div>
            </div>
          )}
          {Object.entries(platformCounts).map(([platform, count]) => (
            <div key={platform} className="dedicated-summary-card info">
              <div className="dedicated-summary-value">{count}</div>
              <div className="dedicated-summary-label">{platform}</div>
            </div>
          ))}
        </div>

        {/* Filter row */}
        <div className="dedicated-filter-row">
          <div className="filter-group">
            <span className="filter-label">Source:</span>
            {(['all', 'crowdstrike', 'jumpcloud'] as const).map(key => (
              <button key={key} className={`filter-tab ${sourceFilter === key ? 'active' : ''}`} onClick={() => setSourceFilter(key)}>
                {key === 'all' ? 'All' : key === 'crowdstrike' ? 'CrowdStrike' : 'JumpCloud Only'}
              </button>
            ))}
          </div>
          <div className="filter-group">
            <span className="filter-label">OS:</span>
            {(['all', 'windows', 'mac', 'linux'] as const).map(key => (
              <button key={key} className={`filter-tab ${osFilter === key ? 'active' : ''}`} onClick={() => setOsFilter(key)}>
                {key === 'all' ? 'All' : key === 'mac' ? 'macOS' : key.charAt(0).toUpperCase() + key.slice(1)}
              </button>
            ))}
          </div>
          <div className="filter-group">
            <span className="filter-label">Status:</span>
            {(['all', 'online', 'offline'] as const).map(key => (
              <button key={key} className={`filter-tab ${statusFilter === key ? 'active' : ''}`} onClick={() => setStatusFilter(key)}>
                {key === 'all' ? 'All' : key.charAt(0).toUpperCase() + key.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="dedicated-search-wrapper">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
          </svg>
          <input
            type="text"
            className="dedicated-search-input"
            placeholder={t.devicesSearchPlaceholder}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        {filteredDevices.length === 0 ? (
          <div className="dedicated-section-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5">
              <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8m-4-4v4"/>
            </svg>
            <p>{t.devicesNoDevices}</p>
          </div>
        ) : (
          <div className="dedicated-section-table-wrapper">
            <table className="dedicated-section-table">
              <thead>
                <tr>
                  <th>{t.devicesHostname}</th>
                  <th>{t.devicesOS}</th>
                  <th>IP</th>
                  <th>{t.devicesLastSeen}</th>
                  <th>{t.devicesCSStatus}</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {filteredDevices.map(device => {
                  const online = isOnline(device.last_seen)
                  const hasEDR = device.source !== 'jumpcloud'
                  return (
                    <tr
                      key={device.device_id}
                      className={hasEDR ? 'clickable-row' : ''}
                      onClick={() => hasEDR && onDeviceClick(device.device_id)}
                    >
                      <td className="font-medium">
                        <span className="platform-icon"><PlatformIcon platform={device.platform_name} />{device.hostname}</span>
                      </td>
                      <td className="text-muted">
                        {device.platform_name}
                        {device.os_version ? ` ${device.os_version}` : ''}
                      </td>
                      <td className="text-muted text-nowrap">{device.local_ip || device.external_ip || '--'}</td>
                      <td className="text-muted text-nowrap">{formatDate(device.last_seen)}</td>
                      <td>
                        <span className={`status-badge ${online ? 'success' : 'warning'}`}>
                          <span className={`status-dot ${online ? 'online' : 'offline'}`} />
                          {online ? t.devicesOnline : t.devicesOffline}
                        </span>
                      </td>
                      <td>{getSourceBadge(device.source)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
